import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Clock, Trash2, Download, Edit2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function exportCsv(logs) {
  const header = ["Date", "Client", "Task", "Staff", "Hours", "Billable", "Description"];
  const rows = logs.map((l) => [
    l.date || "",
    l.client_name || "",
    l.request_title || "",
    l.user_name || "",
    l.hours || 0,
    l.is_billable ? "Yes" : "No",
    `"${(l.description || "").replace(/"/g, '""')}"`,
  ]);
  const csv = [header.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `time-logs-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function TimeTracking() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ request_id: "", hours: "", description: "", date: new Date().toISOString().slice(0, 10), is_billable: true });
  const [saving, setSaving] = useState(false);
  const [filterClient, setFilterClient] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ hours: "", date: "", description: "", is_billable: true });
  const [editSaving, setEditSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadLogs = () =>
    api.get("/timelogs").then((r) => setLogs(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    Promise.all([loadLogs(), api.get("/requests"), api.get("/clients")]).then(([, r, c]) => {
      setRequests(r.data);
      setClients(c.data);
    });
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/timelogs", { ...form, hours: parseFloat(form.hours) });
      setLogs((prev) => [r.data, ...prev]);
      setShowDialog(false);
      setForm({ request_id: "", hours: "", description: "", date: new Date().toISOString().slice(0, 10), is_billable: true });
      toast.success("Time logged");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to log time");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/timelogs/${id}`);
      setLogs((prev) => prev.filter((l) => l.id !== id));
      toast.success("Time log deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  const openEdit = (log) => {
    setEditTarget(log);
    setEditForm({ hours: String(log.hours), date: log.date, description: log.description || "", is_billable: log.is_billable });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const r = await api.put(`/timelogs/${editTarget.id}`, {
        hours: parseFloat(editForm.hours),
        date: editForm.date,
        description: editForm.description,
        is_billable: editForm.is_billable,
      });
      setLogs((prev) => prev.map((l) => l.id === editTarget.id ? { ...l, ...r.data } : l));
      setEditTarget(null);
      toast.success("Time log updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setEditSaving(false);
    }
  };

  const filtered = filterClient ? logs.filter((l) => l.client_id === filterClient) : logs;
  const totalHours = filtered.reduce((s, l) => s + (l.hours || 0), 0);

  // Group by date
  const grouped = filtered.reduce((acc, log) => {
    const date = log.date || "Unknown";
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort().reverse();

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="time-tracking-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold" style={{ color: "#292524" }}>Time Tracking</h1>
            <p className="text-sm mt-0.5" style={{ color: "#57534E" }}>
              {totalHours.toFixed(1)} hours total
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="export-csv-button"
              variant="outline"
              onClick={() => exportCsv(filtered)}
              disabled={filtered.length === 0}
              className="flex items-center gap-2"
            >
              <Download size={14} strokeWidth={1.5} />
              Export CSV
            </Button>
            <Button
              data-testid="log-time-button"
              onClick={() => setShowDialog(true)}
              className="flex items-center gap-2"
              style={{ background: "#292524", color: "#fff" }}
            >
              <Plus size={15} strokeWidth={1.5} />
              Log Time
            </Button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-3 mb-6">
          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm outline-none"
            style={{ borderColor: "#E7E5E4", background: "#fff", color: "#292524" }}
          >
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Logs grouped by date */}
        {loading ? (
          <div className="text-center py-12 text-sm" style={{ color: "#A8A29E" }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <Clock size={32} className="mx-auto mb-3" style={{ color: "#E7E5E4" }} />
            <p className="text-sm" style={{ color: "#A8A29E" }}>No time logged yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {sortedDates.map((date) => {
              const dayLogs = grouped[date];
              const dayTotal = dayLogs.reduce((s, l) => s + (l.hours || 0), 0);
              return (
                <div key={date}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "#A8A29E" }}>
                      {new Date(date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                    <p className="text-sm font-medium" style={{ color: "#57534E" }}>{dayTotal.toFixed(1)}h</p>
                  </div>
                  <div className="space-y-2">
                    {dayLogs.map((log) => (
                      <div
                        key={log.id}
                        data-testid={`timelog-row-${log.id}`}
                        onClick={() => openEdit(log)}
                        className="bg-white rounded-xl border p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow group"
                        style={{ borderColor: "#E7E5E4" }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-right min-w-10">
                            <span className="font-serif text-xl font-bold" style={{ color: "#292524" }}>{log.hours}</span>
                            <p className="text-xs" style={{ color: "#A8A29E" }}>hrs</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium" style={{ color: "#292524" }}>{log.request_title}</p>
                            <p className="text-xs mt-0.5" style={{ color: "#A8A29E" }}>
                              {log.client_name}{log.description && ` · ${log.description}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {log.is_billable && (
                            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#059669" }}>
                              Billable
                            </span>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full hidden sm:inline-block" style={{ background: "#F5F3EF", color: "#57534E" }}>
                            {log.user_name}
                          </span>
                          <Edit2 size={13} className="opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: "#57534E" }} />
                          <button
                            data-testid={`delete-timelog-${log.id}`}
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(log); }}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                            style={{ color: "#A8A29E" }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>        <DialogContent data-testid="log-time-dialog">
          <DialogHeader><DialogTitle className="font-serif">Log Time</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label>Task *</Label>
              <select
                data-testid="log-request-select"
                required
                value={form.request_id}
                onChange={(e) => setForm({ ...form, request_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: "#E7E5E4", background: "#fff" }}
              >
                <option value="">Select a request</option>
                {requests.filter((r) => !["completed", "cancelled"].includes(r.status)).map((r) => (
                  <option key={r.id} value={r.id}>{r.client_name} — {r.title}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hours *</Label>
                <Input data-testid="hours-input" type="number" min="0.1" step="0.1" required value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} placeholder="1.5" />
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description of work done..." />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="billable-dialog" checked={form.is_billable} onChange={(e) => setForm({ ...form, is_billable: e.target.checked })} />
              <Label htmlFor="billable-dialog">Billable</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="submit-log-time" type="submit" disabled={saving} style={{ background: "#292524", color: "#fff" }}>
                {saving ? "Logging..." : "Log Time"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit time log dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="edit-timelog-dialog">
          <DialogHeader><DialogTitle className="font-serif">Edit Time Log</DialogTitle></DialogHeader>
          <form onSubmit={handleEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hours *</Label>
                <Input data-testid="edit-hours-input" type="number" min="0.1" step="0.1" required
                  value={editForm.hours} onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })} />
              </div>
              <div>
                <Label>Date *</Label>
                <Input type="date" required value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Brief description..." />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="edit-billable" checked={editForm.is_billable} onChange={(e) => setEditForm({ ...editForm, is_billable: e.target.checked })} />
              <Label htmlFor="edit-billable">Billable</Label>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => { setEditTarget(null); setDeleteTarget(editTarget); }}>
                Delete
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditTarget(null)}>Cancel</Button>
              <Button data-testid="save-timelog-edit" type="submit" disabled={editSaving} style={{ background: "#292524", color: "#fff" }}>
                {editSaving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-timelog-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Time Log</AlertDialogTitle>
            <AlertDialogDescription>
              Remove this {deleteTarget?.hours}h log for "{deleteTarget?.request_title}"? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-timelog-confirm"
              onClick={() => handleDelete(deleteTarget?.id)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
